import {
  CcfAlert,
  CcfAttachmentDownloadIcon,
  CcfBox,
  CcfCloseIcon,
  CcfSimulatorUploadIcon,
  CcfSnackBar,
  CcfTypography,
} from '@nice-ccf/ui-controls';
import styles from './simulator-v2-copilot-tab.module.scss';
import { generateDate } from '../../utils/utils';
import { TransitionGroup } from 'react-transition-group';
import { useDispatch, useSelector } from 'react-redux';
import { getSelectedContact } from '../../+state/simulatorV2.slice';
import { DigitalChannelType, MediaType } from '@nice-ccf/core-sdk';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Slide,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { ReactNode, useEffect, useRef, useState } from 'react';
import {
  CcfEmailProgressBar,
  getIsLiveCopilotEnabled,
} from '@nice-ccf/ui-components';
import axios from 'axios';
import {
  CopilotFormData,
  ConversationMessage,
  IntentAndAssistance,
  SuggestionArticle,
} from './copilot-form-editor/types';
import { CopilotSummariesSection } from './copilot-form-editor/CopilotSummariesSection';
import { CopilotConversationSection } from './copilot-form-editor/CopilotConversationSection';
import { CopilotJourneyHistorySection } from './copilot-form-editor/CopilotJourneyHistorySection';
import { CopilotBotTranscriptSection } from './copilot-form-editor/CopilotBotTranscriptSection';

/* eslint-disable-next-line */
export interface SimulatorV2CopilotTabProps {
  setUpdatedContact: any;
  updatedContact: any;
  /**
   * Reports whether the Copilot form currently passes validation, so the
   * parent's top-level Save can be blocked when required fields are empty.
   * Reported `true` whenever the manual editor is not shown (nothing to validate).
   */
  setIsCopilotValid?: (valid: boolean) => void;
  /**
   * Build Manually's working copy, held in the parent so it survives this tab
   * remounting. NOT part of updatedContact, so it never enters Redux or the
   * agent broadcast. Kept independent of the "Upload JSON" path.
   */
  copilotManualDraft?: CopilotFormData;
  setCopilotManualDraft?: (draft: CopilotFormData) => void;
  /**
   * Per-media snapshot of copilot contact fields captured just before
   * Journey Details clears them on media/channel change. Key: `${media}__${channel}`.
   * Allows restoring saved data when the user switches media and switches back.
   */
  copilotSnapshotByMedia?: Record<string, any>;
}

/**
 * Collapsible wrapper that gives each Copilot form section its own titled
 * accordion. All sections start expanded.
 * @param props - title and children to render inside the accordion
 * @example `<SectionAccordion title="Summaries">...</SectionAccordion>`
 */
function SectionAccordion({
  title,
  children,
}: {
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <Accordion
      defaultExpanded
      disableGutters
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        boxShadow: 'none',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: '#f5f7f9',
          minHeight: 44,
          '& .MuiAccordionSummary-content': { my: 1 },
        }}
      >
        <CcfTypography variant="subtitle2" sx={{ fontWeight: 600, color: '#3f5c69' }}>
          {title}
        </CcfTypography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 2 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

/**
 * True when a string is missing or contains only whitespace.
 * @param v - the value to test
 * @example isBlank('  ') // true
 */
const isBlank = (v?: string) => !v || v.trim() === '';

/**@example */
const emptyFormData = (): CopilotFormData => ({
  rtSummary: '',
  finalAutoSummary: '',
  transferSummary: '',
  autopilotSummary: '',
  conversationMessages: [],
  journeyHistory: { journeySummaryText: '', interactions: [] },
  botTranscriptData: [],
});

/**
 * used to show the Copilot Tab
 * @param props - SimulatorV2CopilotTabProps
 * @example ` <SimulatorV2CopilotTab />`
 */
export function SimulatorV2CopilotTab(props: SimulatorV2CopilotTabProps) {
  const {
    setUpdatedContact,
    updatedContact,
    setIsCopilotValid,
    copilotManualDraft,
    setCopilotManualDraft,
    copilotSnapshotByMedia,
  } = props;
  const selectedContact: any = useSelector(getSelectedContact);
  const isLiveCopilotEnabled = useSelector(getIsLiveCopilotEnabled);

  const [customFileName, setCustomFileName] = useState(
    selectedContact?.copilotCustomFileName || ''
  );

  const [chatMessage, setChatMessage] = useState('');
  const fileUpload = useRef<any>(null);

  const [replyAttachmentList, setreplyAttachmentList] = useState([
    {
      name: selectedContact?.copilotCustomFileName || '',
      progress: 0,
    },
  ]);

  const [formData, setFormData] = useState<CopilotFormData>(emptyFormData());
  const [formDirty, setFormDirty] = useState(false);
  const [uploadConfirmOpen, setUploadConfirmOpen] = useState(false);
  const [templateLoadError, setTemplateLoadError] = useState(false);
  const [editMode, setEditMode] = useState<'ui' | 'upload'>('ui');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [saveToast, setSaveToast] = useState('');
  const isMediaChannelInitialRunRef = useRef(true);
  // Becomes true once the Build Manually form has had its initial load, so the
  // draft-sync effect doesn't overwrite a saved draft with the empty starting
  // state before hydration runs.
  const draftHydratedRef = useRef(false);

  useEffect(() => {
    handleInputChange('isCopilotFileUpload', customFileName ? true : false);
    handleInputChange('copilotCustomFileName', customFileName || '');
  }, [customFileName]);

  // On mount (and remount after tab change): restore persisted edits or load default file.
  useEffect(() => {
    if (
      selectedContact?.copilotAssist === 'Yes' &&
      selectedContact?.copilotSource === 'JSON'
    ) {
      setTemplateLoadError(false);
      // Restore priority for the Build Manually editor:
      // 1. Its own draft (survives this tab's remount, independent of uploads).
      // 2. The saved journey content (when editing an existing journey).
      // 3. Per-media snapshot — data captured before Journey Details cleared it
      //    on a media/channel switch; restores saved content when switching back.
      // 4. The default template (fresh journey or new media with no prior data).
      const snapshotKey = `${selectedContact?.media}__${selectedContact?.channel ?? ''}`;
      const mediaSnapshot = copilotSnapshotByMedia?.[snapshotKey];
      if (copilotManualDraft) {
        setFormData(copilotManualDraft);
        setFormDirty(false);
        draftHydratedRef.current = true;
      } else if (updatedContact?.customerChatReplyJsonData?.length > 0) {
        hydrateFromContact(updatedContact);
      } else if (mediaSnapshot?.customerChatReplyJsonData?.length > 0) {
        // dirty=true so the persistence effect syncs it back into updatedContact
        hydrateFromContact(mediaSnapshot, true);
      } else {
        fetchAndSetDefaultFile();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On media/channel change while mounted: restore using the same priority as
  // the mount effect (draft → saved contact data → snapshot → default file).
  // This fires when switching between saved journeys with different media; in
  // that case the new journey's data is already in updatedContact, so we must
  // prefer it over the default template.
  // Skips on the initial run because the mount effect above already handled it.
  useEffect(() => {
    if (isMediaChannelInitialRunRef.current) {
      isMediaChannelInitialRunRef.current = false;
      return;
    }
    if (
      selectedContact?.copilotAssist === 'Yes' &&
      selectedContact?.copilotSource === 'JSON'
    ) {
      setTemplateLoadError(false);
      const snapshotKey = `${selectedContact?.media}__${selectedContact?.channel ?? ''}`;
      const mediaSnapshot = copilotSnapshotByMedia?.[snapshotKey];
      if (copilotManualDraft) {
        setFormData(copilotManualDraft);
        setFormDirty(false);
        draftHydratedRef.current = true;
      } else if (updatedContact?.customerChatReplyJsonData?.length > 0) {
        hydrateFromContact(updatedContact);
      } else if (mediaSnapshot?.customerChatReplyJsonData?.length > 0) {
        hydrateFromContact(mediaSnapshot, true);
      } else {
        fetchAndSetDefaultFile();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContact?.media, selectedContact?.channel]);

  /**
   * function  for Attachment Progress bar
   * @param param0 - progressValue
   * @param param1 - attachmentId
   * @example progressCallback(30,1)
   * @returns
   */
  function progressCallback(progressValue: any, attachmentId: number) {
    setreplyAttachmentList(
      replyAttachmentList.map((attachment: any, index: number) => {
        if (index === attachmentId) {
          return { ...attachment, progress: progressValue };
        }
        return attachment;
      })
    );
    setreplyAttachmentList((state) => {
      return state;
    });
  }

  /**
   * Function to get file info of attchment being uploaded
   * @example getFileInfo()
   */
  const handleInputChange = (name: string, value: any) => {
    if (name === 'copilotAssist' && value === 'No') {
      setCustomFileName('');
      setreplyAttachmentList([
        {
          name: '',
          progress: 0,
        },
      ]);
      if (fileUpload?.current) {
        fileUpload.current.value = '';
      }
      setUpdatedContact((prevState: any) => {
        return {
          ...prevState,
          [name]: value,
          botTranscript: false,
          journeyHistory: false,
          showtransferSummary: false,
          lastUpdatedDate: generateDate(),
          isCopilotFileUpload: false,
          copilotCustomFileName: '',
        };
      });
    } else {
      setUpdatedContact((prevState: any) => {
        return {
          ...prevState,
          lastUpdatedDate: generateDate(),
          [name]: value,
        };
      });
    }
  };

  /**@example */
  const hydrateFormData = (newArr: any) => {
    // Pick whichever conversation array the file actually contains. The correct
    // template was already selected upstream, so we trust the file's own keys
    // rather than selectedContact (which reflects saved Redux state, not the
    // live UI media/channel selection).
    const conversationMessages: ConversationMessage[] =
      newArr?.copilotAgentAndCustomerVoiceData ??
      newArr?.copilotAgentAndCustomerChatData ??
      newArr?.copilotAgentAndCustomerEmailData ??
      [];

    setFormData({
      rtSummary: newArr?.rtSummary ?? '',
      finalAutoSummary: newArr?.finalAutoSummary ?? '',
      transferSummary: newArr?.agentTransferSummary ?? '',
      autopilotSummary: newArr?.autopilotSummary ?? '',
      conversationMessages,
      journeyHistory: newArr?.journeyHistory ?? {
        journeySummaryText: '',
        interactions: [],
      },
      botTranscriptData: newArr?.botTranscriptData ?? [],
    });
    setFormDirty(false);
    draftHydratedRef.current = true;
  };

  /**@example */
  const hydrateFromContact = (contact: any, dirty = false) => {
    setFormData({
      rtSummary: contact?.rtSummary ?? '',
      finalAutoSummary: contact?.autoSummaryText ?? '',
      transferSummary: contact?.transferSummary ?? '',
      autopilotSummary: contact?.autopilotSummary ?? '',
      conversationMessages: contact?.customerChatReplyJsonData ?? [],
      journeyHistory: contact?.perviousInteractionJsonData ?? { journeySummaryText: '', interactions: [] },
      botTranscriptData: contact?.botTranscriptData ?? [],
    });
    setFormDirty(dirty);
    draftHydratedRef.current = true;
  };

  /**@example */
  const updateFormData = (updater: (prev: CopilotFormData) => CopilotFormData) => {
    setFormData(updater);
    setFormDirty(true);
    // Clear stale validation errors as soon as the user starts editing.
    setValidationErrors((prev) => (prev.length ? [] : prev));
  };

  // Debounced: propagate form edits into updatedContact. Without the debounce
  // every keystroke triggers a dashboard re-render + Redux dispatch, which
  // causes the whole child tree to re-render and makes text fields feel laggy.
  useEffect(() => {
    if (!formDirty) return;
    const timer = setTimeout(() => {
      setUpdatedContact((prevState: any) => ({
        ...prevState,
        lastUpdatedDate: generateDate(),
        rtSummary: formData.rtSummary,
        autoSummaryText: formData.finalAutoSummary,
        transferSummary: formData.transferSummary,
        autopilotSummary: formData.autopilotSummary,
        customerChatReplyJsonData: formData.conversationMessages,
        perviousInteractionJsonData: formData.journeyHistory,
        botTranscriptData: formData.botTranscriptData,
      }));
    }, 350);
    return () => clearTimeout(timer);
  }, [formData]);

  // Mirror the Build Manually form into the parent-held draft so it survives a
  // tab remount. Guarded so it doesn't clobber a saved draft with the empty
  // starting state before the initial hydration. An upload never changes
  // formData, so this never captures uploaded content.
  // Also debounced: the draft only needs to be current when the tab unmounts,
  // so there is no value in writing it on every keystroke.
  useEffect(() => {
    if (!draftHydratedRef.current) return;
    const timer = setTimeout(() => {
      setCopilotManualDraft?.(formData);
    }, 350);
    return () => clearTimeout(timer);
  }, [formData]);

  /**
   * Function to get file info of attchment being uploaded
   * @example getFileInfo()
   */
  const getFileInfo = () => {
    const data = new FormData();
    data.append('file', fileUpload.current.files);
    const attachmentCopy = JSON.parse(
      JSON.stringify(replyAttachmentList)
    ).filter((x: any) => x.name !== '');
    for (const item of fileUpload.current.files) {
      setCustomFileName(item.name);
      attachmentCopy.push({ name: item.name, progress: 0 });
    }
    setreplyAttachmentList([...attachmentCopy]);
    getFileContent();
    handleInputChange('customerChatReply', '');
    fileUpload.current.value = '';
  };

  /**
   * True when the form currently holds any content that an upload would
   * overwrite. Used instead of `formDirty` because that flag is reset whenever
   * the tab remounts (inactive CcfTabs panels unmount) or the form re-hydrates,
   * which would silently skip the overwrite confirmation.
   * @example hasFormContent()
   */
  const hasFormContent = () =>
    formData.conversationMessages.length > 0 ||
    formData.botTranscriptData.length > 0 ||
    !isBlank(formData.rtSummary) ||
    !isBlank(formData.finalAutoSummary) ||
    !isBlank(formData.transferSummary) ||
    !isBlank(formData.autopilotSummary) ||
    !isBlank(formData.journeyHistory?.journeySummaryText);

  /**@example */
  const handleFileInputChange = () => {
    if (hasFormContent() && fileUpload.current?.files?.[0]) {
      setUploadConfirmOpen(true);
      return;
    }
    getFileInfo();
  };

  /**@example */
  const confirmUploadOverwrite = () => {
    setUploadConfirmOpen(false);
    getFileInfo();
  };

  /**@example */
  const cancelUploadOverwrite = () => {
    setUploadConfirmOpen(false);
    if (fileUpload?.current) fileUpload.current.value = '';
  };

  /**
   * Function to receive text file content of json file being uploaded.
   * @param e - FileReader load event
   * @param fromUpload - true when the user uploaded a file via "Upload JSON".
   *   In that case we make the uploaded data the effective (saved) data but do
   *   NOT touch the Build Manually form (`formData`), so the two modes stay
   *   independent. The default-template load (fromUpload = false) still
   *   hydrates the Build Manually form so it has a starting point.
   * @example receivedText(e, true)
   */
  const receivedText = (e: any, fromUpload = false) => {
    if (!isLiveCopilotEnabled) {
      const lines = e.target.result;
      const newArr = JSON.parse(lines);
      const conversationData =
        newArr.copilotAgentAndCustomerVoiceData ??
        newArr.copilotAgentAndCustomerChatData ??
        newArr.copilotAgentAndCustomerEmailData;
      const perviousInteractions = newArr.journeyHistory;
      const realTimeSummary = newArr.rtSummary;
      const autopilotSummary = newArr.autopilotSummary;
      const transferSummary = newArr.agentTransferSummary;
      const botTranscriptFile = newArr.botTranscriptData;
      const finalAutoSummary = newArr.finalAutoSummary;
      handleInputChange('customerChatReplyJsonData', conversationData);
      handleInputChange('perviousInteractionJsonData', perviousInteractions);
      handleInputChange('rtSummary', realTimeSummary);
      handleInputChange('autopilotSummary', autopilotSummary);
      handleInputChange('transferSummary', transferSummary);
      handleInputChange('botTranscriptData', botTranscriptFile);
      handleInputChange('autoSummaryText', finalAutoSummary);
      if (botTranscriptFile) {
        handleInputChange('botTranscript', updatedContact?.botTranscript);
      }
      if (!fromUpload) {
        hydrateFormData(newArr);
      }
    }
  };

  // Add this helper function
  /**@example */
  const fetchAndSetDefaultFile = async () => {
    const fileName =
      selectedContact.channel === 'Chat' &&
      selectedContact?.media === MediaType.DIGITAL &&
      selectedContact.copilotAssist === 'Yes' &&
      selectedContact.copilotSource === 'JSON'
        ? 'CopilotChatData.json'
        : selectedContact?.media === MediaType.VOICE &&
          selectedContact.copilotAssist === 'Yes' &&
          selectedContact.copilotSource === 'JSON'
        ? 'CopilotVoiceData.json'
        : 'ChatData.json';

    try {
      setTemplateLoadError(false);
      const response = await axios({
        url: './../assets/' + fileName,
        method: 'GET',
        responseType: 'blob',
      });
      const file = new File([response.data], fileName, {
        type: 'application/json',
      });
      const fr = new FileReader();
      fr.onload = receivedText;
      fr.readAsText(file);
    } catch (err) {
      console.error('Failed to fetch default file:', err);
      handleInputChange('customerChatReplyJsonData', null);
      setCustomFileName('');
      setreplyAttachmentList([{ name: '', progress: 0 }]);
      setTemplateLoadError(true);
    }
  };

  /**
   * Function to get file content of JSON file being uploaded
   * @example getFileContent()
   */
  const getFileContent = () => {
    const file = fileUpload.current.files[0];
    const fr = new FileReader();
    fr.onload = (ev) => receivedText(ev, true);
    fr.readAsText(file);
  };

  /**
   * function  to remove attachment
   * @param param0 - param
   * @example attachmentRemoveHandler()
   * @returns
   */
  function attachmentRemoveHandler(attachmentItem: any) {
    const updatedAttachmentList = replyAttachmentList.filter(
      (attach: any) => attach.name !== attachmentItem
    );
    setreplyAttachmentList(updatedAttachmentList);
    if (fileUpload?.current) {
      fileUpload.current.value = '';
    }
    setCustomFileName('');
    fetchAndSetDefaultFile();
  }

  /**
   * Function to download JSON file template
   * @example - downloadChatJsonFile()
   */
  const downloadChatJsonFile = () => {
    const fileName =
      selectedContact.channel === 'Chat' &&
      selectedContact?.media === MediaType.DIGITAL &&
      selectedContact.copilotAssist === 'Yes' &&
      selectedContact.copilotSource === 'JSON'
        ? 'CopilotChatData.json'
        : selectedContact?.media === MediaType.VOICE &&
          selectedContact.copilotAssist === 'Yes' &&
          selectedContact.copilotSource === 'JSON'
        ? 'CopilotVoiceData.json'
        : 'ChatData.json';
    axios({
      url: './../assets/' + fileName,
      method: 'GET',
      responseType: 'blob',
    })
      .then((response: any) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((err: any) => {
        console.error('Error downloading template:', err);
      });
  };

  /**
   * Function to download the current (possibly cloned/modified) JSON file
   * so the user can edit it offline and re-upload via the existing flow.
   * @example - downloadCurrentJsonFile()
   */
  const downloadCurrentJsonFile = () => {
    const contact = updatedContact ?? selectedContact;
    if (!contact) {
      return;
    }

    const isVoice = contact?.media === MediaType.VOICE;
    const isChat =
      contact?.media === MediaType.DIGITAL &&
      contact?.channel === DigitalChannelType.CHAT;
    const conversationKey = isVoice
      ? 'copilotAgentAndCustomerVoiceData'
      : isChat
      ? 'copilotAgentAndCustomerChatData'
      : 'copilotAgentAndCustomerEmailData';

    const reconstructedJson: Record<string, unknown> = {
      journeyHistory: contact?.perviousInteractionJsonData ?? null,
    };
    if (isChat) {
      reconstructedJson.botTranscriptData =
        contact?.botTranscriptData ?? null;
    }
    reconstructedJson.rtSummary = contact?.rtSummary ?? null;
    reconstructedJson.finalAutoSummary = contact?.autoSummaryText ?? null;
    reconstructedJson.agentTransferSummary =
      contact?.transferSummary ?? null;
    if (contact?.autopilotSummary != null) {
      reconstructedJson.autopilotSummary = contact.autopilotSummary;
    }
    if (!isChat && contact?.botTranscriptData != null) {
      reconstructedJson.botTranscriptData = contact.botTranscriptData;
    }
    reconstructedJson[conversationKey] =
      contact?.customerChatReplyJsonData ?? [];

    const fileName =
      contact?.copilotCustomFileName ||
      (contact?.media === MediaType.VOICE
        ? 'CopilotVoiceData.json'
        : contact?.media === MediaType.DIGITAL &&
          contact?.channel === DigitalChannelType.CHAT
        ? 'CopilotChatData.json'
        : 'ChatData.json');

    const blob = new Blob([JSON.stringify(reconstructedJson, null, 2)], {
      type: 'application/json',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  /**
   * Validates that every field the user can edit (in enabled sections) is
   * non-empty. Returns a list of human-readable messages for whatever is empty;
   * an empty list means the form is valid.
   * @example const errors = validateForm();
   */
  const validateForm = (): string[] => {
    const errors: string[] = [];

    // Summaries
    if (isBlank(formData.rtSummary)) errors.push('RT Summary is empty');
    if (isBlank(formData.finalAutoSummary))
      errors.push('Final Auto Summary is empty');
    if (updatedContact?.showtransferSummary && isBlank(formData.transferSummary))
      errors.push('Agent Transfer Summary is empty');

    // Journey History (only when the section is enabled)
    if (
      updatedContact?.journeyHistory &&
      isBlank(formData.journeyHistory?.journeySummaryText)
    )
      errors.push('Journey Summary Text is empty');

    // Conversation messages (elevated email/SMS are hidden, so skipped)
    formData.conversationMessages.forEach((msg, idx) => {
      const label = `Conversation message ${idx + 1}`;
      switch (msg.recordType) {
        case 'nbr':
        case 'customerReply':
        case 'behavioralGuidance': {
          const arr = (msg.message as string[]) ?? [];
          if (arr.length === 0 || arr.some((s) => isBlank(s)))
            errors.push(`${label}: message text is empty`);
          break;
        }
        case 'sentiment': {
          const arr = msg.message as Array<{ description: string }>;
          if (!arr?.[0] || isBlank(arr[0].description))
            errors.push(`${label}: sentiment description is empty`);
          break;
        }
        case 'suggestionArticle': {
          const art = (msg.message as SuggestionArticle[])?.[0];
          if (
            !art ||
            isBlank(art.kbAnswers?.title) ||
            isBlank(art.kbAnswers?.description)
          )
            errors.push(`${label}: article title/description is empty`);
          (art?.kbLinks ?? []).forEach((lnk, li) => {
            if (isBlank(lnk.text) || isBlank(lnk.link))
              errors.push(`${label}: link ${li + 1} is incomplete`);
          });
          (art?.kbImages ?? []).forEach((img, ii) => {
            if (isBlank(img.name) || isBlank(img.imageUrl))
              errors.push(`${label}: image ${ii + 1} is incomplete`);
          });
          break;
        }
        case 'intentAndAssistance': {
          const ia = (msg.message as IntentAndAssistance[])?.[0];
          if (!ia || (isBlank(ia.intent) && isBlank(ia.title)))
            errors.push(`${label}: task goal is empty`);
          (ia?.tasks ?? []).forEach((t, ti) => {
            if (isBlank(t.text))
              errors.push(`${label}: step ${ti + 1} message is empty`);
            (t.buttons ?? []).forEach((b, bi) => {
              if (isBlank(b.text))
                errors.push(
                  `${label}: step ${ti + 1} option ${bi + 1} is empty`
                );
            });
          });
          break;
        }
        default:
          break;
      }
    });

    // Bot Transcript (only when the section is enabled)
    if (updatedContact?.botTranscript) {
      formData.botTranscriptData.forEach((m, idx) => {
        if (isBlank(m.message))
          errors.push(`Bot Transcript message ${idx + 1} is empty`);
      });
    }

    return errors;
  };

  // Report validity up to the parent so the top-level Save can be blocked when
  // required fields are empty. Only enforce when the manual editor is actually
  // shown (Copilot active AND Voice or Digital/Chat); otherwise report valid so
  // non-Copilot journeys and hidden-editor cases save normally.
  useEffect(() => {
    const editorVisible =
      updatedContact?.copilotAssist === 'Yes' &&
      (updatedContact?.media === MediaType.VOICE ||
        (updatedContact?.media === MediaType.DIGITAL &&
          updatedContact?.channel === DigitalChannelType.CHAT));
    setIsCopilotValid?.(editorVisible ? validateForm().length === 0 : true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData,
    updatedContact?.copilotAssist,
    updatedContact?.showtransferSummary,
    updatedContact?.journeyHistory,
    updatedContact?.botTranscript,
    updatedContact?.media,
    updatedContact?.channel,
  ]);

  /**@example */
  const saveAndApply = () => {
    const errors = validateForm();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors([]);

    handleInputChange('customerChatReplyJsonData', formData.conversationMessages);
    handleInputChange('perviousInteractionJsonData', formData.journeyHistory);
    handleInputChange('rtSummary', formData.rtSummary);
    handleInputChange('autoSummaryText', formData.finalAutoSummary);
    handleInputChange('transferSummary', formData.transferSummary);
    handleInputChange('autopilotSummary', formData.autopilotSummary);
    handleInputChange('botTranscriptData', formData.botTranscriptData);
    setFormDirty(false);
    setSaveToast(
      'Copilot changes are saved. You can now click Save to complete the journey.'
    );
  };

  /**@example */
  const downloadFormAsJson = () => {
    const isVoice = selectedContact?.media === MediaType.VOICE;
    const isChat =
      selectedContact?.media === MediaType.DIGITAL &&
      selectedContact?.channel === DigitalChannelType.CHAT;
    const conversationKey = isVoice
      ? 'copilotAgentAndCustomerVoiceData'
      : isChat
      ? 'copilotAgentAndCustomerChatData'
      : 'copilotAgentAndCustomerEmailData';

    const json: Record<string, unknown> = {
      journeyHistory: formData.journeyHistory,
      botTranscriptData: formData.botTranscriptData,
      rtSummary: formData.rtSummary,
      finalAutoSummary: formData.finalAutoSummary,
      agentTransferSummary: formData.transferSummary,
      [conversationKey]: formData.conversationMessages,
    };
    if (formData.autopilotSummary) {
      json.autopilotSummary = formData.autopilotSummary;
    }

    const blob = new Blob([JSON.stringify(json, null, 2)], {
      type: 'application/json',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'CopilotData-custom.json');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  /**@example */
  const getDownloadTemplate = () => {
    return (
      <span
        style={{
          color: '#007AB8',
          textDecorationLine: 'underline',
        }}
        onClick={downloadChatJsonFile}
        id={`sm_copilot_customerChat_downloadTemplate`}
      >
        Click here
      </span>
    );
  };

  return (
    <div className={styles['container']}>
      <CcfBox style={{ padding: '20px', paddingBottom: '0px' }}>
        <CcfTypography className={styles.headerText}>
          Copilot Options
        </CcfTypography>{' '}
        <CcfBox style={{ margin: '15px 0px 0px' }}>
          <CcfBox className={styles.containerGrid}>
            <>
              <CcfBox className={styles.togglesGrid}>
              <FormControlLabel
                label="Activate Copilot"
                labelPlacement="start"
                className={styles.simLabelStyles}
                control={
                  <Switch
                    name="copilotAssist"
                    color="primary"
                    checked={
                      selectedContact.copilotAssist === 'Yes' ? true : false
                    }
                    sx={{
                      ' .MuiFormControlLabel-label': {
                        width: 165,
                      },
                    }}
                    style={{ marginLeft: 0 }}
                    onChange={(event) =>
                      handleInputChange(
                        'copilotAssist',
                        event?.target.checked === true ? 'Yes' : 'No'
                      )
                    }
                  />
                }
              />
              {updatedContact.copilotAssist === 'Yes' &&
                updatedContact.media !== 'VideoCall' && (
                  <>
                    <FormControlLabel
                      label="Journey History"
                      labelPlacement="start"
                      className={styles.simLabelStyles}
                      control={
                        <Switch
                          id="sm_contactDetails_journeyHistory"
                          name="journeyHistory"
                          color="primary"
                          checked={updatedContact?.journeyHistory}
                          sx={{
                            ' .MuiFormControlLabel-label': {
                              width: 165,
                            },
                          }}
                          style={{ marginLeft: 0 }}
                          onChange={(e) =>
                            handleInputChange(
                              'journeyHistory',
                              e.target.checked
                            )
                          }
                        />
                      }
                    />
                    <FormControlLabel
                      label="Agent Transfer Summary"
                      labelPlacement="start"
                      className={styles.simLabelStyles}
                      control={
                        <Switch
                          id="sm_contactDetails_showtransferSummary"
                          name="showtransferSummary"
                          color="primary"
                          checked={updatedContact?.showtransferSummary}
                          sx={{
                            ' .MuiFormControlLabel-label': {
                              width: 165,
                            },
                          }}
                          style={{ marginLeft: 0 }}
                          onChange={(e) =>
                            handleInputChange(
                              'showtransferSummary',
                              e.target.checked
                            )
                          }
                        />
                      }
                    />
                    {selectedContact.media === MediaType.DIGITAL &&
                      selectedContact.channel === DigitalChannelType.CHAT && (
                        <FormControlLabel
                          label="Bot Transcript"
                          labelPlacement="start"
                          className={styles.simLabelStyles}
                          control={
                            <Switch
                              id="sm_contactDetails_botTranscript"
                              name="BotTranscript"
                              color="primary"
                              checked={updatedContact?.botTranscript}
                              sx={{
                                ' .MuiFormControlLabel-label': {
                                  width: 165,
                                },
                              }}
                              style={{ marginLeft: 0 }}
                              onChange={(e) =>
                                handleInputChange(
                                  'botTranscript',
                                  e.target.checked
                                )
                              }
                            />
                          }
                        />
                      )}
                  </>
                )}
              </CcfBox>

                    {updatedContact.copilotAssist === 'Yes' &&
                      (updatedContact.media === MediaType.VOICE ||
                        (updatedContact.media === MediaType.DIGITAL &&
                          updatedContact.channel ===
                            DigitalChannelType.CHAT)) && (
                      <CcfBox sx={{ width: '100%', pr: 1 }}>

                        {/* Mode toggle: customize in the UI or upload a JSON file */}
                        <CcfBox sx={{ mb: 2 }}>
                          <ToggleButtonGroup
                            exclusive
                            size="small"
                            color="primary"
                            value={editMode}
                            onChange={(_e, value) => value && setEditMode(value)}
                            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 2 } }}
                          >
                            <ToggleButton value="ui">Build Manually</ToggleButton>
                            <ToggleButton value="upload">Upload JSON</ToggleButton>
                          </ToggleButtonGroup>
                        </CcfBox>

                        {editMode === 'ui' && (
                          <>
                        {/* ── Form Editor Zone ── */}
                        <CcfBox sx={{ mt: 1, mb: 1 }}>
                          {templateLoadError && (
                            <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
                              Could not load default template. You can edit fields manually below or upload a file.
                            </Alert>
                          )}

                          <CcfBox sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 9 }}>
                            <SectionAccordion title="Summaries">
                              <CopilotSummariesSection
                                data={{
                                  rtSummary: formData.rtSummary,
                                  finalAutoSummary: formData.finalAutoSummary,
                                  transferSummary: formData.transferSummary,
                                  autopilotSummary: formData.autopilotSummary,
                                }}
                                onChange={(field, value) =>
                                  updateFormData((prev) => ({ ...prev, [field]: value }))
                                }
                                transferSummaryDisabled={!updatedContact?.showtransferSummary}
                              />
                            </SectionAccordion>

                            <SectionAccordion title="Journey History">
                              <CopilotJourneyHistorySection
                                data={formData.journeyHistory}
                                onChange={(jh) =>
                                  updateFormData((prev) => ({ ...prev, journeyHistory: jh }))
                                }
                                disabled={!updatedContact?.journeyHistory}
                              />
                            </SectionAccordion>

                            <SectionAccordion
                              title={`Conversation Messages (${formData.conversationMessages.length})`}
                            >
                              <CopilotConversationSection
                                messages={formData.conversationMessages}
                                media={
                                  selectedContact?.media === MediaType.VOICE
                                    ? 'voice'
                                    : 'digital'
                                }
                                onChange={(msgs) =>
                                  updateFormData((prev) => ({ ...prev, conversationMessages: msgs }))
                                }
                              />
                            </SectionAccordion>

                            {selectedContact?.media === MediaType.DIGITAL &&
                              selectedContact?.channel === DigitalChannelType.CHAT && (
                                <SectionAccordion
                                  title={`Bot Transcript (${formData.botTranscriptData.length})`}
                                >
                                  <CopilotBotTranscriptSection
                                    messages={formData.botTranscriptData}
                                    onChange={(msgs) =>
                                      updateFormData((prev) => ({ ...prev, botTranscriptData: msgs }))
                                    }
                                    disabled={!updatedContact?.botTranscript}
                                  />
                                </SectionAccordion>
                              )}
                          </CcfBox>

                          {/* Sticky Save Bar */}
                          <CcfBox className={styles.stickyActionBar}>
                            <CcfBox sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                              {validationErrors.length > 0 && (
                                <Alert
                                  severity="error"
                                  sx={{ fontSize: 12, py: 0 }}
                                  onClose={() => setValidationErrors([])}
                                >
                                  Please fill in all fields before saving:
                                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                                    {validationErrors.slice(0, 6).map((err, i) => (
                                      <li key={i}>{err}</li>
                                    ))}
                                    {validationErrors.length > 6 && (
                                      <li>…and {validationErrors.length - 6} more</li>
                                    )}
                                  </ul>
                                </Alert>
                              )}
                              <CcfBox sx={{ display: 'flex', gap: 1.5 }}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={saveAndApply}
                                  sx={{ textTransform: 'none', backgroundColor: '#007AB8', '&:hover': { backgroundColor: '#005f8e' } }}
                                >
                                  Save &amp; Apply
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={downloadFormAsJson}
                                  sx={{ textTransform: 'none' }}
                                >
                                  Download JSON
                                </Button>
                              </CcfBox>
                            </CcfBox>
                          </CcfBox>
                        </CcfBox>
                          </>
                        )}

                        {editMode === 'upload' && (
                          <>
                        <>
                          <CcfTypography className={styles.txtChatMsg}>
                            Customer Chat Simulator
                          </CcfTypography>
                        </>
                        <CcfBox
                          className={
                            replyAttachmentList?.length > 0
                              ? styles.uploadedFileBox
                              : styles.uploadFileBox
                          }
                          style={{
                            cursor: chatMessage ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {((replyAttachmentList &&
                            replyAttachmentList.every(
                              (x: any) => x.name && x.name.trim() === ''
                            )) ||
                            customFileName === '' ||
                            selectedContact?.copilotCustomFileName === '' ||
                            updatedContact?.copilotCustomFileName === '') && (
                            <div
                              className={styles.uploadFileInnerBox}
                              onClick={() => {
                                fileUpload.current.click();
                              }}
                            >
                              <input
                                type="file"
                                id={`sm_copilot_customerChat_uploadTemplate`}
                                hidden
                                ref={fileUpload}
                                accept="application/json"
                                multiple
                                onChange={handleFileInputChange}
                                disabled={chatMessage.length > 0}
                              />
                              <CcfSimulatorUploadIcon />
                              <CcfTypography
                                className={styles.txtUploadTemplate}
                              >
                                Upload template
                              </CcfTypography>
                            </div>
                          )}
                          {(replyAttachmentList.length > 0 &&
                            replyAttachmentList.every(
                              (item) => item.name && item.name.trim() !== ''
                            )) ||
                          customFileName !== '' ||
                          selectedContact?.copilotCustomFileName?.trim() !==
                            '' ||
                          updatedContact?.copilotCustomFileName?.trim() !==
                            '' ? (
                            <TransitionGroup>
                              <CcfTypography className={styles.txtFile}>
                                File:
                              </CcfTypography>
                              {replyAttachmentList.map(
                                (attachmentItem: any, index: number) => {
                                  return (
                                    <Slide
                                      key={index}
                                      direction={'left'}
                                      timeout={500}
                                    >
                                      <div
                                        className={styles.attachmentBox}
                                        style={{
                                          width: '100%',
                                        }}
                                        key={index}
                                      >
                                        {attachmentItem.progress < 100 ? (
                                          <div
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '8px',
                                              width: '100%',
                                              padding: '4px 0',
                                            }}
                                          >
                                            <CircularProgress
                                              size={16}
                                              thickness={5}
                                              style={{
                                                color: '#007AB8',
                                                flexShrink: 0,
                                              }}
                                            />
                                            <CcfTypography
                                              className={styles.fileNameInBlack}
                                              style={{ width: '100%' }}
                                            >
                                              Uploading {attachmentItem.name}...
                                            </CcfTypography>
                                          </div>
                                        ) : (
                                          <>
                                            <CcfTypography
                                              className={
                                                styles.fileNameUploaded
                                              }
                                              style={{ width: '100%' }}
                                            >
                                              {attachmentItem.name}
                                            </CcfTypography>
                                            <span
                                              id={`sm_copilot_customerChat_downloadCurrent`}
                                              title="Download current JSON"
                                              className={
                                                styles.attachmentDownloadIcon
                                              }
                                              onClick={downloadCurrentJsonFile}
                                            >
                                              <CcfAttachmentDownloadIcon />
                                            </span>
                                            <CcfCloseIcon
                                              className={
                                                styles.attachmentCloseIcon
                                              }
                                              onClick={() => {
                                                attachmentRemoveHandler(
                                                  attachmentItem.name
                                                );
                                              }}
                                            />
                                          </>
                                        )}
                                        <CcfEmailProgressBar
                                          attachmentId={index}
                                          progressCallback={progressCallback}
                                        />
                                      </div>
                                    </Slide>
                                  );
                                }
                              )}
                            </TransitionGroup>
                          ) : null}
                        </CcfBox>
                        <CcfBox className={styles.clickHereStatement}>
                          {selectedContact?.media === MediaType.VOICE ? (
                            <>
                              Please upload a voice interaction JSON file.
                              <br />
                              {getDownloadTemplate()} to download sample
                              template.
                            </>
                          ) : selectedContact?.media === MediaType.DIGITAL &&
                            selectedContact?.channel ===
                              DigitalChannelType.CHAT ? (
                            <>
                              Please upload a chat interaction JSON file. <br />
                              {getDownloadTemplate()} to download sample
                              template.
                            </>
                          ) : (
                            <>
                              {getDownloadTemplate()}
                              &nbsp; to download a sample interaction template.
                            </>
                          )}
                        </CcfBox>
                          </>
                        )}
                      </CcfBox>
                    )}
            </>
          </CcfBox>
        </CcfBox>
      </CcfBox>

      {/* Upload overwrite confirmation dialog */}
      <Dialog
        open={uploadConfirmOpen}
        onClose={cancelUploadOverwrite}
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: 440,
            maxWidth: '90vw',
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            fontSize: 18,
            fontWeight: 600,
            color: '#3f5c69',
            pb: 1,
          }}
        >
          <CcfBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: '#fff4e5',
              flexShrink: 0,
            }}
          >
            <WarningAmberRoundedIcon sx={{ color: '#ed6c02', fontSize: 22 }} />
          </CcfBox>
          Overwrite form data?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#5a6b75', fontSize: 14, lineHeight: 1.6 }}>
            Uploading a new file will <strong>replace all your current
            details</strong>. Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
          <Button
            onClick={cancelUploadOverwrite}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderColor: '#cfd8dc',
              color: '#3f5c69',
              '&:hover': { borderColor: '#90a4ae', backgroundColor: '#f5f7f9' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmUploadOverwrite}
            variant="contained"
            disableElevation
            sx={{
              textTransform: 'none',
            }}
          >
            Replace &amp; Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save & Apply confirmation toast */}
      <CcfSnackBar
        open={saveToast !== ''}
        autoHideDuration={4000}
        onClose={() => setSaveToast('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <div>
          <CcfAlert
            closeAlert={() => setSaveToast('')}
            severity="success"
            style={{
              height: 'auto',
              width: 'auto',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {saveToast}
          </CcfAlert>
        </div>
      </CcfSnackBar>
    </div>
  );
}

export default SimulatorV2CopilotTab;
